import React, {useCallback, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, ControllerRenderProps, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {COUNTRY_CODES} from 'core/constants/countryCodes';
import {IS_TJ} from 'core/constants/env';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {createOrganizationFx} from 'models/auth';
import {DirectorRegistrationRequest, LegalPerson} from 'types';
import {Address} from 'types/geo';
import {ProfileRole, ProfileType} from 'types/profile';
import {mapEnumToI18nOptions} from 'utils/select';

import {
    Actions,
    BackButton,
    CustomSubtitle,
    CustomTitle,
    Field,
    FormFields,
    SubmitButton,
    Wrapper,
} from './styled';

type FormField = {
    name: keyof (Omit<DirectorRegistrationRequest, 'legal_person'> & LegalPerson);
    readonly?: boolean;
    options?: SelectOption<string>[];
};

export const CreateOrganization = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback(
        (key: string) =>
            i18n.exists('pages.register.specificPlaceholders.' + key)
                ? t('pages.register.specificPlaceholders.' + key)
                : t('pages.register.fields.' + key),
        [i18n.language],
    );

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const getAddressInfo = async (address?: string) => {
        if (!address) return;
        try {
            const addressPayload = (partialAddress: string): Address => {
                const lang =
                    (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) ||
                    'ru';
                return {
                    address: partialAddress,
                    lang,
                };
            };

            const payload = addressPayload(address);
            return await getAddressInfoFromString(payload);
        } catch (error) {
            console.error(error);
        }
    };

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                contact_email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .nullable()
                    .notRequired(),
                email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .required(translateValidates('required')),
                password: yup
                    .string()
                    .min(6, translateValidates('password'))
                    .required(translateValidates('required')),
                password_confirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .test('password', translateValidates('confirm_password_mismatch'), function(
                        value,
                    ) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        return value === this.parent.password;
                    }),
                phone: yup
                    .string()
                    .required(translateValidates('required'))
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
                type: yup.mixed<ProfileType.DIRECTOR>().notRequired(),
                tin: yup.string().required(translateValidates('required')),
                // .matches(/^\+?\d{14}$/, translateValidates('tin')),
                ...(IS_TJ && {
                    ein: yup.string().required(translateValidates('required')),
                }),
                gln: yup.string().required(translateValidates('required')),
                country: yup.string().required(translateValidates('required')),
                role: yup.string().required(translateValidates('required')),
                company_name: yup.string().required(translateValidates('required')),
                short_name: yup.string().notRequired(),
                legal_address: yup.string().required(translateValidates('required')),
                postal_code: yup.string().required(translateValidates('required')),
                iban: yup.string().required(translateValidates('required')),
                bic: yup.string().required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        reset,
    } = useForm<Omit<DirectorRegistrationRequest, 'legal_person'> & LegalPerson>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (
        data: Omit<DirectorRegistrationRequest, 'legal_person'> & LegalPerson,
    ) => {
        try {
            const addressInfo = await getAddressInfo(data.legal_address);
            const gps = `${addressInfo?.location.lat ?? ''},${addressInfo?.location.lng ?? ''}`;

            const addressParts = (addressInfo?.formattedAddress ?? '')
                .split(',')
                .map((p) => p.trim())
                .filter(Boolean);
            const _ = addressParts.pop() || '';
            const city = addressParts.pop() || 'NOT_AVAILABLE';
            const street = addressParts.join(', ').trim() || 'NOT_AVAILABLE';

            const payload: DirectorRegistrationRequest = {
                email: data.email,
                password: data.password,
                type: ProfileType.DIRECTOR,
                first_name: data.first_name,
                last_name: data.last_name,
                role: data.role,
                ...(data.middle_name && {middle_name: data.middle_name}),
                ...(data.phone && {phone: data.phone}),
                legal_person: {
                    tin: data.tin,
                    country: data.country,
                    company_name: data.company_name,
                    legal_address: data.legal_address,
                    iban: data.iban,
                    bic: data.bic,
                    city,
                    street,
                    gps,
                    ...(data.ein && {ein: data.ein}),
                    ...(data.gln && {gln: data.gln}),
                    ...(data.vat_id && {vat_id: data.vat_id}),
                    ...(data.short_name && {short_name: data.short_name}),
                    ...(data.postal_code && {postal_code: data.postal_code}),
                },
            };

            await createOrganizationFx(payload);
            reset();
            history.push(ROUTE.ORGANIZATIONS);
        } catch (error) {
            console.log(error);
        }
    };

    const formFields: {
        director: FormField[];
        organization: FormField[];
    } = useMemo(
        () => ({
            director: [
                {name: 'first_name'},
                {name: 'middle_name'},
                {name: 'last_name'},
                {name: 'email'},
                {name: 'password'},
                {name: 'password_confirm'},
                {
                    name: 'role',
                    options: mapEnumToI18nOptions(ProfileRole, 'dictionaries.profileRole'),
                },
                ...(IS_TJ ? [{name: 'ein' as const}] : []),
            ] as FormField[],
            organization: [
                {name: 'tin'},
                {name: 'phone'},
                {name: 'gln'},
                {
                    name: 'country',
                    options: COUNTRY_CODES,
                },
                {name: 'vat_id'},
                {name: 'company_name'},
                {name: 'short_name'},
                {name: 'legal_address'},
                {name: 'postal_code'},
                {name: 'iban'},
                {name: 'bic'},
            ] as FormField[],
        }),
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<
            Omit<DirectorRegistrationRequest, 'legal_person'> & LegalPerson
        >,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        if (field.name === 'legal_address') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <AsyncSelect
                        {...baseProps}
                        menuPlacement="top"
                        name={controlEvents.name}
                        loadOptions={addressOptions}
                        formatCreateLabel={(value: string) =>
                            `${t('pages.products.grid.add')} "${value}"`
                        }
                        isClearable
                        onChange={(v?: {value: string; label: string}) => {
                            controlEvents.onChange(v?.value || '');
                        }}
                        value={
                            controlEvents.value
                                ? {value: controlEvents.value, label: controlEvents.value}
                                : null
                        }
                    />
                </div>
            );
        }

        if (field.name === 'country') {
            return (
                <div style={{paddingBottom: '2px'}}>
                    <Select
                        isSearchable
                        options={field.options}
                        menuPlacement="top"
                        menuShouldBlockScroll
                        {...baseProps}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                    />
                </div>
            );
        }

        if (field.name === 'role') {
            return (
                <div style={{paddingBottom: '2px'}}>
                    <Select
                        options={field.options}
                        {...baseProps}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                    />
                </div>
            );
        }

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.ORGANIZATIONS}>{t('pages.profile.organizations.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.profile.organizations.title')}</CustomTitle>
                        <CustomSubtitle>
                            {t('pages.profile.organizations.personalTitle')}
                        </CustomSubtitle>
                        <FormFields>
                            {formFields.director.map((field) => (
                                <Field
                                    key={field.name}
                                    text={errors?.[field.name]?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <CustomSubtitle>
                            {t('pages.profile.organizations.legalTitle')}
                        </CustomSubtitle>
                        <FormFields>
                            {formFields.organization.map((field) => (
                                <Field
                                    key={field.name}
                                    text={errors?.[field.name]?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.ORGANIZATIONS);
                                    reset();
                                }}
                            >
                                {t('pages.profile.organizations.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.organizations.create.create')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
